import React from 'react';
import { motion } from 'framer-motion';
import { Gavel } from 'lucide-react';
import { Universe } from './slides';

const ease = [0.65, 0, 0.35, 1] as const;
const soft = [0.22, 1, 0.36, 1] as const;

// Themed slide transitions, direction-aware (dir = 1 forward, -1 back)
export const VARIANTS: Record<Universe, any> = {
  app: {
    enter: (dir: number) => ({ x: dir > 0 ? 1600 : -400, opacity: dir > 0 ? 1 : 0.6, zIndex: dir > 0 ? 2 : 1 }),
    center: { x: 0, opacity: 1, transition: { duration: 0.75, ease } },
    exit: (dir: number) => ({ x: dir > 0 ? -400 : 1600, opacity: dir > 0 ? 0.6 : 1, zIndex: dir > 0 ? 1 : 2, transition: { duration: 0.75, ease } }),
  },
  dossier: {
    enter: (dir: number) => ({ y: dir > 0 ? -950 : 30, opacity: dir > 0 ? 1 : 0, zIndex: dir > 0 ? 2 : 1 }),
    center: { y: 0, opacity: 1, transition: { duration: 0.8, ease: soft } },
    exit: (dir: number) => ({ y: dir > 0 ? 30 : -950, opacity: dir > 0 ? 0 : 1, zIndex: dir > 0 ? 1 : 2, transition: { duration: 0.8, ease: dir > 0 ? 'easeOut' : ease } }),
  },
  neutral: {
    enter: { opacity: 0 },
    center: { opacity: 1, transition: { duration: 0.6, ease: soft } },
    exit: { opacity: 0, transition: { duration: 0.5, ease } },
  },
};

const BARS = [8, 19, 31, 44, 57, 66, 78, 89];
export const BREAK_SWITCH_MS = 1500;
export const BREAK_TOTAL_MS = 3400;
export const breakEntry = { v: false };

// Special break between axis I (app) and axis II (court): glitch, gavel slam, flash
export function AxisBreak() {
  return (
    <motion.div className="pointer-events-none absolute inset-0 z-50 overflow-hidden" initial={{ opacity: 1 }} animate={{ opacity: [1, 1, 0] }} transition={{ duration: 3.4, times: [0, 0.8, 1] }} data-testid="axis-break-transition">
      {BARS.map((t, i) => (
        <motion.div key={t} className="absolute left-0 h-[34px] w-full bg-red-600" style={{ top: `${t}%` }}
          initial={{ scaleX: 0, x: i % 2 ? 300 : -300, opacity: 0 }} animate={{ scaleX: [0, 1, 0.3, 1, 0.5, 1, 0], x: [i % 2 ? 300 : -300, 0, 60, -40, 0], opacity: [0, 0.9, 0.4, 0.9, 0.5, 0.9, 0] }} transition={{ duration: 1.1, delay: i * 0.04 }} />
      ))}
      <motion.div className="absolute inset-0 bg-black" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.94, 0.94, 0] }} transition={{ duration: 2.4, delay: 0.7, times: [0, 0.15, 0.8, 1] }} />
      <motion.div className="absolute left-0 right-0 top-[120px] text-center font-type text-[26px] uppercase tracking-[0.5em] text-white/70" initial={{ opacity: 0, y: 10 }} animate={{ opacity: [0, 1, 1, 0], y: 0 }} transition={{ duration: 2, delay: 0.9, times: [0, 0.2, 0.8, 1] }}>
        Axe I <span className="text-blue-500">●</span> → <span className="text-red-500">■</span> Axe II
      </motion.div>
      <motion.div className="absolute left-[620px] top-[200px] origin-bottom-right text-white" initial={{ rotate: 0, scale: 1.2, opacity: 0 }} animate={{ rotate: [0, -75, -75, 8, 0], scale: [1.2, 1.4, 1.4, 1, 1], opacity: [0, 1, 1, 1, 1, 0] }} transition={{ duration: 1.9, delay: 1, times: [0, 0.3, 0.42, 0.5, 0.58, 1] }}>
        <Gavel size={300} strokeWidth={1.6} />
      </motion.div>
      <motion.div className="absolute left-[800px] top-[560px] -ml-[20px] -mt-[20px] h-[40px] w-[40px] rounded-full border-[10px] border-red-600" initial={{ scale: 0, opacity: 0 }} animate={{ scale: [0, 32], opacity: [1, 0] }} transition={{ duration: 1, delay: 1.95, ease: 'easeOut' }} />
      <motion.div className="absolute inset-0 bg-white" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.85, 0] }} transition={{ duration: 0.5, delay: 1.95 }} />
      <motion.div className="stamp absolute left-[430px] top-[640px] bg-transparent text-[64px] !mix-blend-normal" initial={{ scale: 2.4, opacity: 0, rotate: -8 }} animate={{ scale: [2.4, 1, 1], opacity: [0, 1, 1, 0], rotate: -8 }} transition={{ duration: 1.4, delay: 2, times: [0, 0.15, 0.8, 1] }}>
        Audience ouverte
      </motion.div>
    </motion.div>
  );
}
